import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {fetchCookieConsent, saveCookieConsent} from '../actions/cookieConsentActions'
import type {
  CookieConsent,
  CookieConsentApiResponse,
  CookieConsentSource,
} from 'src/app/modules/auth/cookie-consent/types'
import {defaultCookieConsent, readCookieConsent} from 'src/utils/cookieConsent'

type CookieConsentState = {
  consent: CookieConsent
  loading: boolean
  saving: boolean
  hydrated: boolean
  source: CookieConsentSource
}

const getConsentFromPayload = (payload: CookieConsent | CookieConsentApiResponse | null | undefined) => {
  if (!payload) return null
  if ('analytics' in payload) return payload
  return payload.data ?? null
}

const localConsent = readCookieConsent()

const initialState: CookieConsentState = {
  consent: localConsent ?? defaultCookieConsent,
  loading: false,
  saving: false,
  hydrated: false,
  source: 'local',
}

const cookieConsentSlice = createSlice({
  name: 'cookieConsent',
  initialState,
  reducers: {
    setLocalCookieConsent: (state, action: PayloadAction<CookieConsent>) => {
      state.consent = action.payload
      state.hydrated = true
      state.source = 'local'
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCookieConsent.pending, (state) => {
      state.loading = true
    })

    builder.addCase(fetchCookieConsent.fulfilled, (state, action) => {
      const consent = getConsentFromPayload(action.payload)

      state.loading = false
      state.hydrated = true

      if (consent) {
        state.consent = consent
        state.source = 'server'
      }
    })

    builder.addCase(fetchCookieConsent.rejected, (state) => {
      state.loading = false
      state.hydrated = true
      state.source = 'local'
    })

    builder.addCase(saveCookieConsent.pending, (state) => {
      state.saving = true
    })

    builder.addCase(saveCookieConsent.fulfilled, (state, action) => {
      const consent = getConsentFromPayload(action.payload) ?? action.meta.arg

      state.saving = false
      state.hydrated = true

      if (consent) {
        state.consent = consent
        state.source = 'server'
      }
    })

    builder.addCase(saveCookieConsent.rejected, (state) => {
      state.saving = false
    })
  },
})

export const {setLocalCookieConsent} = cookieConsentSlice.actions

export default cookieConsentSlice.reducer
